import { FieldArray, useField } from 'formik';

import { apiOptionToFormOption, isOptionPopulated } from '../../util/questions';
import {
  Question,
  QuestionBlock,
  SurveyVariable,
} from '../../types/domainModels';
import { QuestionFormData, QuestionFormOption } from '../../types/forms';

import AddButton from 'components/common/forms/AddButton';
import Button from '../common/forms/Button';
import FileUpload, { FileUploadBulk } from 'components/common/forms/FileUpload';
import FormGroup from '../common/forms/FormGroup';
import QuestionConcept from './QuestionConcept';

const ConceptField = ({
  concepts,
  question,
  questionBlocks,
  questions,
  variables,
  disabled = false,
  lowerLimit,
  upperLimit,
}: {
  concepts: QuestionFormOption[];
  question: Question | undefined;
  questionBlocks: QuestionBlock[];
  questions: Question[];
  variables?: SurveyVariable[];
  disabled?: boolean;
  lowerLimit?: number;
  upperLimit?: number;
}) => {
  const [, , conceptsHelpers] =
    useField<QuestionFormData['concepts']>('concepts');

  if (disabled) {
    return (
      <div className="flex">
        <Button disabled={disabled} grow hierarchy="secondary-gray" size="md">
          Upload
        </Button>
      </div>
    );
  }

  return (
    <FormGroup
      tooltip={
        <div>
          <p>
            This optional concept will be displayed to the survey respondent
            above the question options.
          </p>
          {disabled && (
            <p className="mt-4 font-bold">
              Disabled: Question pipes concept from previous question.
            </p>
          )}
        </div>
      }
    >
      <FieldArray
        name="concepts"
        render={(arrayHelpers) => {
          return (
            <>
              {concepts.map((_option, index) => {
                return (
                  <QuestionConcept
                    key={index}
                    index={index}
                    onClickRemove={() => {
                      if (!lowerLimit || concepts.length > lowerLimit) {
                        arrayHelpers.remove(index);
                      } else {
                        arrayHelpers.replace(index, apiOptionToFormOption());
                      }
                    }}
                    question={question}
                    questionBlocks={questionBlocks}
                    questions={questions}
                    variables={variables ?? []}
                  />
                );
              })}

              {(upperLimit === undefined || concepts.length < upperLimit) && (
                <>
                  {concepts.length === 0 ? (
                    <FileUpload
                      onLoadFile={(imageData) => {
                        const newConcept = apiOptionToFormOption();
                        newConcept.image.preview = imageData;

                        arrayHelpers.push(newConcept);
                      }}
                      trigger={
                        <a className="flex items-center justify-center border border-green text-green bg-white cursor-pointer font-normal px-3 py-1 rounded h-11">
                          <span className="select-none text-sm">
                            Upload New Concept
                          </span>
                        </a>
                      }
                    />
                  ) : (
                    <div className="flex mt-2 space-x-4">
                      <FileUploadBulk
                        onLoadFiles={(filesData) => {
                          const newConcepts: QuestionFormOption[] = [];
                          for (const concept of concepts) {
                            if (isOptionPopulated(concept)) {
                              newConcepts.push(concept);
                            }
                          }

                          for (const fileData of filesData) {
                            const option = apiOptionToFormOption();
                            option.image.preview = fileData;
                            newConcepts.push(option);
                          }

                          conceptsHelpers.setValue(newConcepts);
                        }}
                        trigger={<AddButton label="Bulk Upload" />}
                      />
                      <AddButton
                        label="Add Concept"
                        onClick={() => {
                          arrayHelpers.push(apiOptionToFormOption());
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          );
        }}
      />
    </FormGroup>
  );
};

export default ConceptField;
